import { useState } from 'react';
import { AlertTriangle, Plus, X } from 'lucide-react';
import { Button, Input, DatePicker } from '@roger/ui-components';
import { CurrencyInput } from '../../CurrencyInput';
import { formatDateToISO, parseISOToLocalDate } from '../../../utils/dateUtils';
import {
  EXTERNAL_COMPUTED_HIPPS_MESSAGE,
  EXTERNAL_DEFAULT_HIPPS_MESSAGE,
  EXTERNAL_MANUAL_HIPPS_LABEL,
} from '../../../constants/billing';
import { PdfServiceLineNotice } from './SavedUb04Preview';
import {
  MAX_UB04_SERVICE_LINES,
  MAX_UB04_SERVICE_LINES_MESSAGE,
  type UB04ServiceLine,
} from './types';
import { newRowId } from './worksheetUtils';

const emptyManualLine = (): UB04ServiceLine => ({
  row_id: newRowId(),
  source_type: 'manual',
  source_id: null,
  row_kind: 'manual',
  revenue_code: '',
  revenue_description_or_ide: '',
  hcpcs_hipps_and_modifiers: '',
  service_date: null,
  units: '1',
  charges: '0.00',
  noncovered_charges: '0.00',
});

export function ServiceLinesSection({
  value,
  onChange,
  disabled = false,
}: {
  value: UB04ServiceLine[];
  onChange: (next: UB04ServiceLine[]) => void;
  disabled?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);

  const update = (index: number, patch: Partial<UB04ServiceLine>) => {
    onChange(
      value.map((line, i) => (i === index ? { ...line, ...patch } : line))
    );
  };

  const addLine = () => {
    if (value.length >= MAX_UB04_SERVICE_LINES) {
      setError(MAX_UB04_SERVICE_LINES_MESSAGE);
      return;
    }
    setError(null);
    onChange([...value, emptyManualLine()]);
  };

  const removeLine = (index: number) => {
    setError(null);
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <section className='space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100/70'>
      <div>
        <h3 className='flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600'>
          <span className='h-1.5 w-1.5 rounded-full bg-teal-500' />
          FL 42-48 Service lines
        </h3>
      </div>
      <PdfServiceLineNotice lineCount={value.length} />
      {error ? <p className='text-sm text-red-600'>{error}</p> : null}
      <div className='space-y-2'>
        {value.map((line, index) => {
          // A generated 0023 row may carry a submission-only HIPPS.
          // The row itself carries the warning and the FL44 input is the
          // escape hatch, so no extra banner or input is added.
          const externalDefault = line.hipps_source === 'external_default';
          const externalComputed = line.hipps_source === 'external_computed';
          const externalManual = line.hipps_source === 'external_manual';
          return (
            <div
              key={line.row_id ?? `manual-${index}`}
              className={`grid gap-2 rounded-lg border p-3 md:grid-cols-[4rem_minmax(8rem,1fr)_8rem_8rem_6rem_6rem_6rem_2rem] ${
                externalDefault || externalComputed
                  ? 'border-amber-300 bg-amber-50/70'
                  : 'border-slate-200 bg-slate-50/70'
              }`}
            >
              <label className='space-y-1 text-xs text-muted-foreground'>
                Rev
                <Input
                  inputSize='sm'
                  value={line.revenue_code}
                  disabled={disabled}
                  onChange={event =>
                    update(index, { revenue_code: event.target.value })
                  }
                />
              </label>
              <label className='space-y-1 text-xs text-muted-foreground'>
                Description
                <Input
                  inputSize='sm'
                  maxLength={24}
                  value={line.revenue_description_or_ide}
                  disabled={disabled}
                  onChange={event =>
                    update(index, {
                      revenue_description_or_ide: event.target.value,
                    })
                  }
                />
              </label>
              <label className='space-y-1 text-xs text-muted-foreground'>
                HCPCS/HIPPS
                <Input
                  inputSize='sm'
                  value={line.hcpcs_hipps_and_modifiers ?? ''}
                  disabled={disabled}
                  onChange={event =>
                    update(index, {
                      hcpcs_hipps_and_modifiers: event.target.value,
                    })
                  }
                />
              </label>
              <label className='space-y-1 text-xs text-muted-foreground'>
                Date
                <DatePicker
                  datePickerSize='sm'
                  typable
                  className='w-full min-w-0'
                  date={parseISOToLocalDate(line.service_date ?? '')}
                  disabled={disabled}
                  onDateChange={date =>
                    update(index, {
                      service_date: date ? formatDateToISO(date) : null,
                    })
                  }
                />
              </label>
              <label className='space-y-1 text-xs text-muted-foreground'>
                Units
                <Input
                  inputSize='sm'
                  inputMode='decimal'
                  value={String(line.units ?? '')}
                  disabled={disabled}
                  onChange={event =>
                    update(index, { units: event.target.value })
                  }
                />
              </label>
              <label className='space-y-1 text-xs text-muted-foreground'>
                Charges
                <CurrencyInput
                  inputSize='sm'
                  value={String(line.charges ?? '')}
                  disabled={disabled}
                  onValueChange={charges =>
                    update(index, {
                      charges,
                      // The server keeps FL 48 equal to Charges on a line
                      // flagged non-covered; show it moving with the edit.
                      ...(line.non_covered
                        ? { noncovered_charges: charges }
                        : {}),
                    })
                  }
                />
              </label>
              <label className='space-y-1 text-xs text-muted-foreground'>
                Non-covered
                <Input
                  inputSize='sm'
                  readOnly
                  tabIndex={-1}
                  value={String(line.noncovered_charges ?? '')}
                />
              </label>
              <div className='flex items-end justify-end'>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  aria-label='Remove service line'
                  disabled={disabled || line.source_type !== 'manual'}
                  onClick={() => removeLine(index)}
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>
              <div className='md:col-span-8 text-xs text-muted-foreground'>
                Source: {line.source_type}
                {line.source_id ? ` · ${line.source_id}` : ''}
              </div>
              {externalDefault || externalComputed ? (
                <p className='flex items-start gap-1 md:col-span-8 text-xs font-medium text-amber-800'>
                  <AlertTriangle
                    aria-hidden='true'
                    className='mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600'
                  />
                  {externalDefault
                    ? EXTERNAL_DEFAULT_HIPPS_MESSAGE
                    : EXTERNAL_COMPUTED_HIPPS_MESSAGE}
                </p>
              ) : externalManual ? (
                <p className='md:col-span-8 text-xs text-muted-foreground'>
                  {EXTERNAL_MANUAL_HIPPS_LABEL}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={addLine}
        disabled={disabled}
      >
        <Plus className='mr-1 h-3 w-3' />
        Add service line
      </Button>
    </section>
  );
}
